import React, {useEffect, useState} from 'react';

// Progress bar component with smooth transitions
const ProgressBar = ({ percentage, currentStep }) => {
    const [displayedPercentage, setDisplayedPercentage] = useState(percentage);

    const TOTAL_DURATION_MINUTES = 5;

    useEffect(() => {
        const totalDurationMs = TOTAL_DURATION_MINUTES * 60 * 1000;
        const intervalTime = 50;
        const steps = totalDurationMs / intervalTime;
        const increment = 100 / steps;

        const interval = setInterval(() => {
            setDisplayedPercentage(prev => {
                if (percentage === 100 && prev < 95) {
                    return prev + 5;
                }

                const next = prev + increment;
                return next >= percentage ? percentage : next;
            });
        }, intervalTime);

        return () => clearInterval(interval);
    }, [percentage]);

    return (
        <>
            <div className="mb-2 flex flex-col items-center">
                <span className="text-sm text-gray-500 mb-1">{currentStep || 'Initializing search...'}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-md h-6 relative overflow-hidden">
                <div
                    className="bg-blue-600 h-6 rounded-md absolute left-0 top-0 transition-all duration-75 ease-linear"
                    style={{width: `${displayedPercentage}%`}}
                />
                <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center">
                    <div className="bg-white/90 border border-white rounded-md px-1 py-0.3 shadow-md backdrop-blur-sm flex items-center justify-center min-w-[30px]">
                        <span className="text-xs font-bold text-black select-none">
                          {Math.round(displayedPercentage)}%
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

// Format search parameter labels for display
const formatParameterLabel = (key, value) => {
    switch (key) {
        case 'employment_type':
            return value === 'full_time' ? 'Full-time' :
                   value === 'part_time' ? 'Part-time' :
                   value === 'contract' ? 'Contract' : value;
        case 'remote_mode':
            return value === 'at_work' ? 'On-site' :
                   value === 'remote' ? 'Remote' :
                   value === 'hybrid' ? 'Hybrid' : value;
        case 'seniority_levels':
            return value === 'mid_senior_level' ? 'Mid Senior Level' :
                   value === 'entry_level' ? 'Entry Level' :
                   value === 'director' ? 'Director' :
                   value === 'associate' ? 'Associate' :
                   value === 'internship' ? 'Internship' : value;
        case 'time_recency':
            return value === 'day' ? 'Last 24 hours' :
                   value === 'week' ? 'Last week' :
                   value === 'month' ? 'Last month' : value;
        default:
            return value;
    }
};

const SearchStatusBox = ({ searchStatusData, setIsResumePopupOpen }) => {
    if (!searchStatusData) return null;

    return (
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-[0_2px_15px_rgba(0,0,0,0.1),0_5px_30px_rgba(0,0,0,0.15)] overflow-hidden border border-gray-100">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-500">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Searching for your perfect jobs</h2>
                        <p className="text-gray-600">We're processing your job search. This usually takes up to 5 minutes.</p>
                    </div>
                </div>
            </div>
            
            {/* Progress Section */}
            <div className="px-8 py-5 border-b border-gray-100">
                <ProgressBar 
                    percentage={searchStatusData.progress_percentage || 0}
                    currentStep={searchStatusData.current_step}
                />
            </div>
            
            {/* Search Parameters */}
            <div className="px-8 py-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Your Search Criteria</h3>
                
                <div className="grid grid-cols-3 gap-x-6 gap-y-4">
                    {/* Row 1 */}
                    {searchStatusData.job_title && (
                        <div>
                            <div className="flex items-start">
                                <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 block">Job Title</span>
                                    <span className="text-sm font-medium text-gray-800">{searchStatusData.job_title}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {searchStatusData.location && (
                        <div>
                            <div className="flex items-start">
                                <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 block">Location</span>
                                    <span className="text-sm font-medium text-gray-800">{searchStatusData.location}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {searchStatusData.employment_type && (
                        <div>
                            <div className="flex items-start">
                                <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 block">Employment Type</span>
                                    <span className="text-sm font-medium text-gray-800">
                                        {formatParameterLabel('employment_type', searchStatusData.employment_type)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Row 2 */}
                    {searchStatusData.remote_mode && (
                        <div>
                            <div className="flex items-start">
                                <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 block">Work Mode</span>
                                    <span className="text-sm font-medium text-gray-800">
                                        {formatParameterLabel('remote_mode', searchStatusData.remote_mode)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {searchStatusData.seniority_levels && (
                        <div>
                            <div className="flex items-start">
                                <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 block">Experience Level</span>
                                    <span className="text-sm font-medium text-gray-800">
                                        {formatParameterLabel('seniority_levels', searchStatusData.seniority_levels)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {searchStatusData.time_recency && (
                        <div>
                            <div className="flex items-start">
                                <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 block">Time Recency</span>
                                    <span className="text-sm font-medium text-gray-800">
                                        {formatParameterLabel('time_recency', searchStatusData.time_recency)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Row 3 */}
                    {searchStatusData.resume_file_url && (
                        <button
                            onClick={() => setIsResumePopupOpen(true)}
                            className="flex items-start hover:bg-blue-50 rounded-lg p-2 transition-colors duration-200 group w-full text-left"
                        >
                            <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500 flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                </svg>
                            </div>
                            <div>
                                <span className="text-xs text-gray-500 block">Resume</span>
                                <span className="text-sm font-medium text-blue-600 group-hover:underline">
                                    View Uploaded Resume
                                </span>
                            </div>
                        </button>
                    )}

                    {searchStatusData.selected_package && (
                        <div>
                            <div className="flex items-start">
                                <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 block">Package</span>
                                    <span className="text-sm font-medium text-gray-800 capitalize">
                                        {searchStatusData.selected_package}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {searchStatusData.created_at && (
                        <div>
                            <div className="flex items-start">
                                <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 block">Search Date</span>
                                    <span className="text-sm font-medium text-gray-800">
                                        {searchStatusData.created_at}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Job Preferences */}
                {searchStatusData.job_preferences && (
                    <div className="mt-6">
                        <div className="flex items-start mb-2">
                            <div className="mr-2 w-5 h-5 mt-0.5 text-blue-500">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <span className="text-xs text-gray-500 block">Job Preferences</span>
                                <div className="text-sm text-gray-800 mt-1 bg-gray-50 p-3 rounded-lg border border-gray-100 max-h-[150px] overflow-y-auto whitespace-pre-line">
                                    {searchStatusData.job_preferences}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchStatusBox; 